import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import VideoPrompt from "../../../../api/VideoPrompt";
import Location from "../../../../api/Location";
import Input from "../../../../components/form/Input";
import MultiSelect from "../../../../components/MultiSelect";
import ConfirmationModal from "../../../../components/modals/ConfirmationModal";
import { TrashIcon, PlusIcon, XMarkIcon, PencilIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

// Global option for all locations
const GLOBAL_OPTION = { label: "Global (All Locations)", value: "global" };

const VideoPromptPanel = ({ callback = false }) => {
  // Locations
  const [locations, setLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);

  // COMMON Location Selection for creating prompts
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationError, setLocationError] = useState(false);

  // Groups
  const [groups, setGroups] = useState([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupError, setGroupError] = useState(false);

  // Add Group Modal
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);

  // Edit Group Modal
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editGroupName, setEditGroupName] = useState("");
  const [isUpdatingGroup, setIsUpdatingGroup] = useState(false);

  // Delete Group Modal
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);

  // Expanded groups state
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [groupsInitialized, setGroupsInitialized] = useState(false);

  // Existing prompts from API
  const [existingPrompts, setExistingPrompts] = useState([]);
  const [existingPromptsOptions, setExistingPromptsOptions] = useState([]);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);

  // Filter by location for viewing
  const [filterLocation, setFilterLocation] = useState(null);

  // Form fields for new prompt (without location - location is common)
  const [promptText, setPromptText] = useState("");

  // Form errors
  const [promptError, setPromptError] = useState(false);

  // Selected existing prompt to add
  const [selectedExistingPrompt, setSelectedExistingPrompt] = useState(null);

  // Prompts array to submit
  const [promptsToSubmit, setPromptsToSubmit] = useState([]);

  // Submit loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delete confirmation modal for prompts to submit list
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [editFormData, setEditFormData] = useState({
    prompt: "",
  });
  const [isUpdating, setIsUpdating] = useState(false);

  // Delete existing prompt modal
  const [openDeleteExistingModal, setOpenDeleteExistingModal] = useState(false);
  const [existingPromptToDelete, setExistingPromptToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load locations
  const loadLocations = async () => {
    setIsLoadingLocations(true);
    try {
      const response = await Location.getAll();
      if (response?.data?.data) {
        const locationOptions = response.data.data.map((loc) => ({
          label: loc.name,
          value: loc.id,
        }));
        // Add Global option at the beginning
        setLocations([GLOBAL_OPTION, ...locationOptions]);
      }
    } catch (error) {
      console.error("Error loading locations:", error);
      toast.error("Failed to load locations");
    } finally {
      setIsLoadingLocations(false);
    }
  };

  // Load video prompt groups
  const loadGroups = async () => {
    setIsLoadingGroups(true);
    try {
      const response = await VideoPrompt.getVideoPromptGroups();
      if (response?.data) {
        // Handle different response formats
        const groupsData = response.data.data || response.data || [];
        const groupOptions = groupsData.map((group) => ({
          label: group.name || group.group_name,
          value: group.id,
        }));
        setGroups(groupOptions);
      }
    } catch (error) {
      console.error("Error loading video prompt groups:", error);
      toast.error("Failed to load video prompt groups");
    } finally {
      setIsLoadingGroups(false);
    }
  };

  // Create new video prompt group
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }

    setIsCreatingGroup(true);
    try {
      const response = await VideoPrompt.createVideoPromptGroup({
        name: newGroupName.trim(),
      });

      // Handle different response formats
      const groupData = response?.data?.data || response?.data;
      if (groupData) {
        const newGroup = {
          label: groupData.name || newGroupName.trim(),
          value: groupData.id,
        };
        setGroups([...groups, newGroup]);
        setSelectedGroup(newGroup);
        setNewGroupName("");
        setShowAddGroupModal(false);
        toast.success("Video prompt group created successfully");
      }
    } catch (error) {
      console.error("Error creating video prompt group:", error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to create video prompt group. Please try again.");
      }
    } finally {
      setIsCreatingGroup(false);
    }
  };

  // Update video prompt group
  const handleUpdateGroup = async () => {
    if (!editGroupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }

    if (!editingGroup) return;

    setIsUpdatingGroup(true);
    try {
      const response = await VideoPrompt.updateVideoPromptGroup(editingGroup.value, {
        name: editGroupName.trim(),
      });

      // Handle different response formats
      const groupData = response?.data?.data || response?.data;
      if (groupData) {
        const updatedGroup = {
          label: groupData.name || editGroupName.trim(),
          value: editingGroup.value,
        };
        
        // Update groups list
        setGroups(groups.map((g) => 
          g.value === editingGroup.value ? updatedGroup : g
        ));
        
        // Update selected group if it's the one being edited
        if (selectedGroup && selectedGroup.value === editingGroup.value) {
          setSelectedGroup(updatedGroup);
        }
        
        setEditGroupName("");
        setEditingGroup(null);
        setShowEditGroupModal(false);
        toast.success("Video prompt group updated successfully");
      }
    } catch (error) {
      console.error("Error updating video prompt group:", error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to update video prompt group. Please try again.");
      }
    } finally {
      setIsUpdatingGroup(false);
    }
  };

  // Delete video prompt group
  const handleDeleteGroup = async () => {
    if (!groupToDelete) return;

    setIsDeletingGroup(true);
    try {
      await VideoPrompt.deleteVideoPromptGroup(groupToDelete.value);
      
      // Remove from groups list
      setGroups(groups.filter((g) => g.value !== groupToDelete.value));
      
      // Clear selected group if it's the one being deleted
      if (selectedGroup && selectedGroup.value === groupToDelete.value) {
        setSelectedGroup(null);
      }
      
      setGroupToDelete(null);
      setShowDeleteGroupModal(false);
      toast.success("Video prompt group deleted successfully");
    } catch (error) {
      console.error("Error deleting video prompt group:", error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to delete video prompt group. Please try again.");
      }
    } finally {
      setIsDeletingGroup(false);
    }
  };

  // Fetch all existing prompts from API
  const loadExistingPrompts = async () => {
    setIsLoadingExisting(true);
    try {
      const response = await VideoPrompt.getAll();
      let promptsData = [];

      // Handle different response formats
      // API returns { content: [...], pageable: {...}, ... }
      if (response?.data?.content) {
        promptsData = response.data.content;
      } else if (response?.data?.data) {
        promptsData = response.data.data;
      } else if (Array.isArray(response?.data)) {
        promptsData = response.data;
      } else if (response?.data && typeof response.data === 'object') {
        promptsData = response.data.prompts || response.data.items || response.data.results || [];
      }

      setExistingPrompts(promptsData);

      // Format for MultiSelect dropdown
      const options = promptsData.map((p) => ({
        label: p.prompt || p.question || p.text || p.title,
        value: p.id,
        data: p,
      }));
      setExistingPromptsOptions(options);
      // Reset initialization flag when prompts are reloaded
      setGroupsInitialized(false);
    } catch (error) {
      console.error("Error loading existing prompts:", error);
      setExistingPrompts([]);
      setExistingPromptsOptions([]);
    } finally {
      setIsLoadingExisting(false);
    }
  };

  // Fetch prompts by location
  const loadPromptsByLocation = async (locationId) => {
    setIsLoadingExisting(true);
    try {
      const response = await VideoPrompt.getByLocationId(locationId);
      let promptsData = [];

      // Handle different response formats
      // API returns { content: [...], pageable: {...}, ... }
      if (response?.data?.content) {
        promptsData = response.data.content;
      } else if (response?.data?.data) {
        promptsData = response.data.data;
      } else if (Array.isArray(response?.data)) {
        promptsData = response.data;
      } else if (response?.data && typeof response.data === 'object') {
        promptsData = response.data.prompts || response.data.items || response.data.results || [];
      }

      setExistingPrompts(promptsData);
      // Reset initialization flag when prompts are reloaded
      setGroupsInitialized(false);
    } catch (error) {
      console.error("Error loading prompts by location:", error);
      setExistingPrompts([]);
    } finally {
      setIsLoadingExisting(false);
    }
  };

  // Handle filter location change
  const handleFilterLocationChange = (selected) => {
    setFilterLocation(selected);
    if (selected) {
      loadPromptsByLocation(selected.value);
    } else {
      loadExistingPrompts();
    }
  };

  useEffect(() => {
    loadLocations();
    loadGroups();
    loadExistingPrompts();
  }, []);

  // Check if Global option is selected
  const isGlobalSelected = (selected) => {
    if (!selected) return false;
    if (Array.isArray(selected)) {
      return selected.some((loc) => loc.value === "global");
    }
    return selected.value === "global";
  };

  // Handle common location selection change
  const handleLocationChange = (selected) => {
    let newSelectedLocations = [];

    if (selected && selected.length > 0) {
      const lastSelected = selected[selected.length - 1];
      if (lastSelected.value === "global") {
        newSelectedLocations = [GLOBAL_OPTION];
      } else {
        // If a specific location is selected, remove Global if it exists
        newSelectedLocations = selected.filter((loc) => loc.value !== "global");
      }
    } else {
      newSelectedLocations = selected || [];
    }

    setSelectedLocations(newSelectedLocations);
    setLocationError(false);

    // Clear group selection if locations are cleared
    if (newSelectedLocations.length === 0) {
      setSelectedGroup(null);
      setPromptsToSubmit([]);
      return;
    }

    // Update all prompts in the list with the new locations
    if (promptsToSubmit.length > 0 && newSelectedLocations.length > 0) {
      const isGlobal = newSelectedLocations.some((loc) => loc.value === "global");
      const locationIds = isGlobal ? null : newSelectedLocations.map((loc) => loc.value);
      const locationNames = isGlobal ? ["Global (All Locations)"] : newSelectedLocations.map((loc) => loc.label);

      const updatedPrompts = promptsToSubmit.map((p) => ({
        ...p,
        locationId: locationIds,
        locationNames: locationNames,
        isGlobal: isGlobal,
      }));

      setPromptsToSubmit(updatedPrompts);
    }
  };

  // Handle group selection change
  const handleGroupChange = (selected) => {
    setSelectedGroup(selected);
    setGroupError(false);
  };

  // Reset prompt form (not location)
  const resetPromptForm = () => {
    setPromptText("");
    setPromptError(false);
  };

  // Add new prompt to the list
  const handleAddNewPrompt = () => {
    let hasError = false;

    if (!selectedLocations || selectedLocations.length === 0) {
      setLocationError(true);
      toast.error("Please select locations first");
      hasError = true;
    }

    if (!selectedGroup) {
      setGroupError(true);
      toast.error("Please select a group");
      hasError = true;
    }

    if (promptText.trim() === "") {
      setPromptError(true);
      hasError = true;
    }

    if (hasError) return;

    // Check if Global is selected - if so, locationId should be null
    const isGlobal = isGlobalSelected(selectedLocations);
    const locationIds = isGlobal ? null : selectedLocations.map((loc) => loc.value);
    const locationNames = isGlobal ? ["Global (All Locations)"] : selectedLocations.map((loc) => loc.label);

    const newPromptObj = {
      id: `new-${Date.now()}`,
      locationId: locationIds,
      locationNames: locationNames,
      groupId: selectedGroup.value,
      groupName: selectedGroup.label,
      prompt: promptText.trim(),
      isNew: true,
      isGlobal: isGlobal,
    };

    setPromptsToSubmit([...promptsToSubmit, newPromptObj]);
    resetPromptForm(); // Only reset prompt form, keep location and group
    toast.success("Prompt added to list");
  };

  // Add existing prompt to the list (will use the common selected locations)
  const handleSelectExistingPrompt = (selected) => {
    if (!selected) return;

    if (!selectedLocations || selectedLocations.length === 0) {
      setLocationError(true);
      toast.error("Please select locations first");
      setSelectedExistingPrompt(null);
      return;
    }

    if (!selectedGroup) {
      setGroupError(true);
      toast.error("Please select a group first");
      setSelectedExistingPrompt(null);
      return;
    }

    // Check if already added
    const alreadyAdded = promptsToSubmit.some(
      (p) => p.existingId === selected.value
    );

    if (alreadyAdded) {
      toast.error("This prompt is already in your list");
      setSelectedExistingPrompt(null);
      return;
    }

    const existingP = selected.data;

    // Use the common selected locations
    const isGlobal = isGlobalSelected(selectedLocations);
    const locationIds = isGlobal ? null : selectedLocations.map((loc) => loc.value);
    const locationNames = isGlobal ? ["Global (All Locations)"] : selectedLocations.map((loc) => loc.label);

    const promptObj = {
      id: `existing-${selected.value}`,
      existingId: selected.value,
      locationId: locationIds,
      locationNames: locationNames,
      groupId: selectedGroup.value,
      groupName: selectedGroup.label,
      prompt: existingP.prompt || existingP.question || existingP.text || existingP.title,
      isNew: false,
      isGlobal: isGlobal,
    };

    setPromptsToSubmit([...promptsToSubmit, promptObj]);
    setSelectedExistingPrompt(null);
    toast.success("Existing prompt added to list");
  };

  // Remove prompt from the list
  const handleRemovePrompt = (index) => {
    const updated = promptsToSubmit.filter((_, i) => i !== index);
    setPromptsToSubmit(updated);
    toast.success("Prompt removed from list");
  };

  // Open delete confirmation for prompts to submit
  const openDeleteConfirmation = (index) => {
    setPromptToDelete(index);
    setOpenDeleteModal(true);
  };

  // Confirm delete for prompts to submit
  const confirmDelete = () => {
    if (promptToDelete !== null) {
      handleRemovePrompt(promptToDelete);
    }
    setOpenDeleteModal(false);
    setPromptToDelete(null);
  };

  // Submit prompts to API
  const handleSubmitPrompts = async () => {
    if (promptsToSubmit.length === 0) {
      toast.error("Please add at least one prompt");
      return;
    }

    setIsSubmitting(true);
    try {
      // Format the payload as required by the API
      // locationId should be an array of location IDs, or null for global prompts
      const payload = promptsToSubmit.map((p) => ({
        locationId: p.isGlobal ? null : (p.locationId || []),
        video_prompts_group_name_id: p.groupId,
        prompt: p.prompt || p.question,
      }));

      const response = await VideoPrompt.create(payload);
      if (response?.data) {
        toast.success("Prompts submitted successfully!");
        setPromptsToSubmit([]);
        setSelectedLocations([]); // Reset locations after successful submit
        setSelectedGroup(null); // Reset group after successful submit
        loadExistingPrompts(); // Refresh the list
      }
    } catch (error) {
      console.error("Error submitting prompts:", error);
      toast.error("Failed to submit prompts. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clear all prompts from the list
  const handleClearAll = () => {
    setPromptsToSubmit([]);
    toast.success("All prompts cleared");
  };

  // ============ EDIT FUNCTIONALITY ============

  // Open edit modal
  const handleEditPrompt = (prompt) => {
    setEditingPrompt(prompt);

    setEditFormData({
      prompt: prompt.prompt || prompt.question || prompt.text || prompt.title || "",
    });
    setShowEditModal(true);
  };

  // Submit edit
  const handleUpdatePrompt = async () => {
    if (editFormData.prompt.trim() === "") {
      toast.error("Prompt text is required");
      return;
    }

    setIsUpdating(true);
    try {
      // Use the original locationIds from the editing prompt
      // API expects locationIds (plural) as an array
      const originalLocationIds = editingPrompt.locationId || editingPrompt.locationIds || editingPrompt.location_id;
      
      // Convert to array if needed, or use null for global prompts
      let locationIdsArray = null;
      if (originalLocationIds !== null && originalLocationIds !== undefined) {
        locationIdsArray = Array.isArray(originalLocationIds) 
          ? originalLocationIds 
          : (originalLocationIds ? [originalLocationIds] : null);
      }

      const payload = {
        locationIds: locationIdsArray,
        prompt: editFormData.prompt.trim(),
      };

      const response = await VideoPrompt.update(editingPrompt.id, payload);
      if (response?.data) {
        toast.success("Prompt updated successfully!");
        setShowEditModal(false);
        setEditingPrompt(null);
        loadExistingPrompts(); // Refresh the list
      }
    } catch (error) {
      console.error("Error updating prompt:", error);
      toast.error("Failed to update prompt. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  // ============ DELETE EXISTING PROMPT FUNCTIONALITY ============

  // Open delete confirmation for existing prompt
  const handleDeleteExistingPrompt = (prompt) => {
    setExistingPromptToDelete(prompt);
    setOpenDeleteExistingModal(true);
  };

  // Confirm delete existing prompt
  const confirmDeleteExisting = async () => {
    if (!existingPromptToDelete) return;

    setIsDeleting(true);
    try {
      const response = await VideoPrompt.delete(existingPromptToDelete.id);
      if (response?.data) {
        toast.success("Prompt deleted successfully!");
        loadExistingPrompts(); // Refresh the list
      }
    } catch (error) {
      console.error("Error deleting prompt:", error);
      toast.error("Failed to delete prompt. Please try again.");
    } finally {
      setIsDeleting(false);
      setOpenDeleteExistingModal(false);
      setExistingPromptToDelete(null);
    }
  };

  // Get location names from IDs
  const getLocationNames = (prompt) => {
    const locationIds = prompt.locationIds || prompt.locationId || prompt.location_id;

    // If locationIds is null or empty array, it's a Global prompt
    if (locationIds === null || (Array.isArray(locationIds) && locationIds.length === 0)) {
      return ["Global (All Locations)"];
    }

    if (!Array.isArray(locationIds)) return [];

    return locations
      .filter((loc) => loc.value !== "global" && locationIds.includes(loc.value))
      .map((loc) => loc.label);
  };

  // Group prompts by videoPromptGroupNameId or group array
  const groupPromptsByGroup = (prompts) => {
    const grouped = {};
    const ungrouped = [];

    prompts.forEach((prompt) => {
      let groupId = null;
      let groupName = null;

      // Handle different response formats:
      // 1. videoPromptGroupNameId (camelCase) - from getAll()
      // 2. video_prompts_group_name_id (snake_case) - alternative format
      // 3. group (array) - from getByLocationId()
      if (prompt.videoPromptGroupNameId) {
        groupId = prompt.videoPromptGroupNameId;
        // Find group name from groups list
        const group = groups.find((g) => g.value === groupId);
        groupName = group ? group.label : `Group ${groupId}`;
      } else if (prompt.video_prompts_group_name_id) {
        groupId = prompt.video_prompts_group_name_id;
        // Find group name from groups list
        const group = groups.find((g) => g.value === groupId);
        groupName = group ? group.label : `Group ${groupId}`;
      } else if (prompt.group && Array.isArray(prompt.group) && prompt.group.length > 0) {
        // Handle group array format
        const firstGroup = prompt.group[0];
        groupId = firstGroup.id;
        groupName = firstGroup.name || `Group ${groupId}`;
      }
      
      if (!groupId) {
        ungrouped.push(prompt);
        return;
      }

      if (!grouped[groupId]) {
        grouped[groupId] = {
          id: groupId,
          name: groupName,
          prompts: [],
        };
      }
      
      grouped[groupId].prompts.push(prompt);
    });

    const groupedArray = Object.values(grouped);
    
    // Initialize expanded groups only once - collapse all if more than 1 group
    if (!groupsInitialized && groupedArray.length > 0) {
      if (groupedArray.length > 1) {
        // All groups start collapsed - Set is already empty
        setGroupsInitialized(true);
      } else if (groupedArray.length === 1) {
        // If only 1 group, expand it by default
        setExpandedGroups(new Set([groupedArray[0].id]));
        setGroupsInitialized(true);
      }
    }

    return { grouped: groupedArray, ungrouped };
  };

  // Toggle group expansion
  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Helper to check if group is expanded
  const isExpanded = (groupId) => expandedGroups.has(groupId);

  return (
    <div className="my-8">
      {/* Delete modal for prompts to submit */}
      <ConfirmationModal
        openTrigger={openDeleteModal}
        closeTrigger={() => setOpenDeleteModal(false)}
        type="error"
        title="Confirm Action"
        confirmText="Delete"
        description="Are you sure you want to remove this prompt from the list?"
        confirm={confirmDelete}
      />

      {/* Delete modal for existing prompts */}
      {openDeleteExistingModal && existingPromptToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => {
                setOpenDeleteExistingModal(false);
                setExistingPromptToDelete(null);
              }}
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Prompt</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    This action cannot be undone.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setOpenDeleteExistingModal(false);
                    setExistingPromptToDelete(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-700 mb-2">
                  Are you sure you want to delete this prompt?
                </p>
                <div className="bg-gray-50 rounded-md p-3 border border-gray-200">
                  <p className="text-sm font-medium text-gray-900">
                    {existingPromptToDelete.prompt || existingPromptToDelete.question || existingPromptToDelete.text || existingPromptToDelete.title}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setOpenDeleteExistingModal(false);
                    setExistingPromptToDelete(null);
                  }}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteExisting}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <i className="fad fa-circle-notch fa-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Group Modal */}
      {showAddGroupModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => {
              setShowAddGroupModal(false);
              setNewGroupName("");
            }} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Add New Group</h3>
                <button
                  onClick={() => {
                    setShowAddGroupModal(false);
                    setNewGroupName("");
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="flex flex-col gap-4">
                {/* Group Name Input */}
                <div>
                  <Input
                    name="groupName"
                    label="Group Name"
                    type="text"
                    value={newGroupName}
                    placeholder="Enter group name..."
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleCreateGroup();
                      }
                    }}
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddGroupModal(false);
                      setNewGroupName("");
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateGroup}
                    disabled={isCreatingGroup || !newGroupName.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-md transition-all disabled:opacity-50 flex items-center gap-2"
                  >
                    {isCreatingGroup ? (
                      <>
                        <i className="fad fa-circle-notch fa-spin" />
                        Creating...
                      </>
                    ) : (
                      "Create Group"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Group Modal */}
      {showEditGroupModal && editingGroup && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => {
              setShowEditGroupModal(false);
              setEditingGroup(null);
              setEditGroupName("");
            }} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Edit Group</h3>
                <button
                  onClick={() => {
                    setShowEditGroupModal(false);
                    setEditingGroup(null);
                    setEditGroupName("");
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="flex flex-col gap-4">
                {/* Group Name Input */}
                <div>
                  <Input
                    name="editGroupName"
                    label="Group Name"
                    type="text"
                    value={editGroupName}
                    placeholder="Enter group name..."
                    onChange={(e) => setEditGroupName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleUpdateGroup();
                      }
                    }}
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditGroupModal(false);
                      setEditingGroup(null);
                      setEditGroupName("");
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleUpdateGroup}
                    disabled={isUpdatingGroup || !editGroupName.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-md transition-all disabled:opacity-50 flex items-center gap-2"
                  >
                    {isUpdatingGroup ? (
                      <>
                        <i className="fad fa-circle-notch fa-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update Group"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Group Modal */}
      {showDeleteGroupModal && groupToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => {
              setShowDeleteGroupModal(false);
              setGroupToDelete(null);
            }} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-red-600">Delete Group</h3>
                <button
                  onClick={() => {
                    setShowDeleteGroupModal(false);
                    setGroupToDelete(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-700 mb-2">
                  Are you sure you want to delete the group <strong>"{groupToDelete.label}"</strong>?
                </p>
                <p className="text-sm text-gray-500">
                  This action cannot be undone. All prompts associated with this group will remain but will no longer be grouped.
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteGroupModal(false);
                    setGroupToDelete(null);
                  }}
                  disabled={isDeletingGroup}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteGroup}
                  disabled={isDeletingGroup}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {isDeletingGroup ? (
                    <>
                      <i className="fad fa-circle-notch fa-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowEditModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Edit Prompt</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="flex flex-col gap-4">
                {/* Prompt Text */}
                <div>
                  <Input
                    name="editPromptText"
                    label="Prompt Text"
                    type="text"
                    value={editFormData.prompt}
                    placeholder="Enter your prompt here..."
                    
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, prompt: e.target.value })
                    }
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleUpdatePrompt}
                    disabled={isUpdating}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-400 rounded-md transition-all disabled:opacity-50 flex items-center gap-2"
                  >
                    {isUpdating ? (
                      <>
                        <i className="fad fa-circle-notch fa-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update Prompt"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-6">
        <p className="text-xl font-bold">Video Prompts Management</p>

        {/* Existing Prompts Section */}
        <div className="border border-gray-200 p-4 rounded-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <p className="text-lg font-semibold">Existing Groups</p>

            {/* Filter by Location */}
            <div className="md:w-64">
              <div className="border border-warmgray-300 rounded-md bg-white">
                <MultiSelect
                  options={locations.filter(loc => loc.value !== "global")}
                  onChange={handleFilterLocationChange}
                  value={filterLocation}
                  menuPlacement="bottom"
                  isLoading={isLoadingLocations}
                  placeholder="Filter by location..."
                  isClearable={true}
                />
              </div>
            </div>
          </div>

          {isLoadingExisting ? (
            <div className="flex items-center gap-2">
              <i className="fad fa-circle-notch fa-spin" />
              <span>Loading existing prompts...</span>
            </div>
          ) : existingPrompts.length > 0 ? (
            <div className="space-y-3">
              {(() => {
                const { grouped, ungrouped } = groupPromptsByGroup(existingPrompts);

                return (
                  <>
                    {/* Grouped Prompts */}
                    {grouped.map((group) => (
                      <div key={group.id} className="border border-gray-300 rounded-lg overflow-hidden">
                        <div
                          className="px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-200"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => toggleGroup(group.id)}>
                              <button
                                type="button"
                                className="text-gray-600 hover:text-gray-900 transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleGroup(group.id);
                                }}
                              >
                                {isExpanded(group.id) ? (
                                  <ChevronUpIcon className="h-5 w-5" />
                                ) : (
                                  <ChevronDownIcon className="h-5 w-5" />
                                )}
                              </button>
                              <div>
                                <h3 className="text-base font-semibold text-gray-900">
                                  {group.name}
                                </h3>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {group.prompts.length} prompt{group.prompts.length !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() => {
                                  const groupOption = groups.find((g) => g.value === group.id);
                                  if (groupOption) {
                                    setEditingGroup(groupOption);
                                    setEditGroupName(groupOption.label);
                                    setShowEditGroupModal(true);
                                  }
                                }}
                                className="text-blue-500 hover:text-blue-700 transition-all p-1"
                                title="Edit group"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const groupOption = groups.find((g) => g.value === group.id);
                                  if (groupOption) {
                                    setGroupToDelete(groupOption);
                                    setShowDeleteGroupModal(true);
                                  }
                                }}
                                className="text-red-500 hover:text-red-700 transition-all p-1"
                                title="Delete group"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                        {isExpanded(group.id) && (
                          <div className="divide-y divide-gray-200 bg-white">
                            {group.prompts.map((p, index) => (
                              <div key={p.id} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start gap-2">
                                      <span className="text-xs text-gray-500 font-medium mt-0.5 flex-shrink-0">
                                        {index + 1}.
                                      </span>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 mb-1.5 line-clamp-2">
                        {p.prompt || p.question || p.text || p.title}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                                      className="text-blue-500 hover:text-blue-700 transition-all p-1"
                            onClick={() => handleEditPrompt(p)}
                            title="Edit prompt"
                          >
                                      <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                                      className="text-red-500 hover:text-red-700 transition-all p-1"
                            onClick={() => handleDeleteExistingPrompt(p)}
                            title="Delete prompt"
                          >
                                      <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Ungrouped Prompts */}
                    {ungrouped.length > 0 && (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-base font-semibold text-gray-900">
                                Ungrouped Prompts
                              </h3>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {ungrouped.length} prompt{ungrouped.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="divide-y divide-gray-200 bg-white">
                          {ungrouped.map((p, index) => (
                            <div key={p.id} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start gap-2">
                                    <span className="text-xs text-gray-500 font-medium mt-0.5 flex-shrink-0">
                                      {index + 1}.
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 mb-1.5 line-clamp-2">
                                        {p.prompt || p.question || p.text || p.title}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button
                                    type="button"
                                    className="text-blue-500 hover:text-blue-700 transition-all p-1"
                                    onClick={() => handleEditPrompt(p)}
                                    title="Edit prompt"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    className="text-red-500 hover:text-red-700 transition-all p-1"
                                    onClick={() => handleDeleteExistingPrompt(p)}
                                    title="Delete prompt"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          ) : (
            <p className="text-gray-500 italic">
              No prompts found.{" "}
              {filterLocation && "Try removing the location filter or add new prompts."}
            </p>
          )}
        </div>

        {/* Create Prompts Section */}
        <div className="border border-gray-200 p-4 rounded-lg">
          <p className="text-lg font-semibold mb-4">Create New Prompts</p>

          <div className="flex flex-col gap-4">
            {/* Step 1: Location and Group Selection */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-md font-semibold mb-2 text-blue-800">
                Step 1: Select Locations and Group
              </p>
              <p className="text-sm text-blue-600 mb-3">
                All prompts you add below will be created for these selected locations and associated with the selected group.
              </p>

              {/* Desktop: Same row layout */}
              <div className="flex flex-col md:flex-row gap-4">
                {/* Location Selection */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Locations
                  </label>
                <div className={`border rounded-md ${locationError ? 'border-red-500' : 'border-warmgray-300'} bg-white`}>
                  <MultiSelect
                    key="locations"
                    options={locations}
                    onChange={handleLocationChange}
                    value={selectedLocations}
                    menuPlacement="bottom"
                    isLoading={isLoadingLocations}
                    placeholder="Select locations or Global..."
                    isMulti={true}
                  />
                </div>
                {locationError && (
                  <p className="text-red-500 text-sm mt-1">Please select at least one location or Global</p>
                )}
                {selectedLocations.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    <span className="text-sm text-gray-600">Selected: </span>
                    {selectedLocations.map((loc, i) => (
                      <span
                        key={i}
                        className={`px-2 py-0.5 rounded text-xs font-medium ${loc.value === "global"
                          ? "bg-green-100 text-green-800"
                          : "bg-primary-100 text-primary-800"
                          }`}
                      >
                        {loc.label}
                      </span>
                    ))}
                  </div>
                )}
                </div>

                {/* Group Selection */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Group <span className="text-red-500">*</span>
                  </label>
                  <div className={`border rounded-md ${groupError ? 'border-red-500' : selectedLocations.length > 0 ? 'border-warmgray-300' : 'border-gray-300'} ${selectedLocations.length === 0 ? 'bg-gray-100' : 'bg-white'}`}>
                    <MultiSelect
                      key="groups"
                      options={groups}
                      onChange={handleGroupChange}
                      value={selectedGroup}
                      menuPlacement="bottom"
                      isLoading={isLoadingGroups}
                      placeholder={selectedLocations.length > 0 ? "Select a group..." : "Select locations first..."}
                      disabledSelect={selectedLocations.length === 0}
                    />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (selectedLocations.length > 0) {
                          setShowAddGroupModal(true);
                        }
                      }}
                      disabled={selectedLocations.length === 0}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 whitespace-nowrap ${selectedLocations.length > 0
                        ? 'bg-primary-500 text-white hover:bg-primary-600 cursor-pointer'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                      <PlusIcon className="h-4 w-4" />
                      <span>Add Group</span>
                    </button>
                    {selectedGroup && selectedLocations.length > 0 && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingGroup(selectedGroup);
                            setEditGroupName(selectedGroup.label);
                            setShowEditGroupModal(true);
                          }}
                          className="px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 bg-primary-500 text-white hover:bg-primary-600"
                          title="Edit group"
                        >
                          <PencilIcon className="h-4 w-4" />
                          <span>Edit</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setGroupToDelete(selectedGroup);
                            setShowDeleteGroupModal(true);
                          }}
                          className="px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 bg-red-500 text-white hover:bg-red-600"
                          title="Delete group"
                        >
                          <TrashIcon className="h-4 w-4" />
                          <span>Delete</span>
                        </button>
                      </>
                    )}
                  </div>
                  {groupError && (
                    <p className="text-red-500 text-sm mt-1">Please select a group</p>
                  )}
                  {selectedGroup && selectedLocations.length > 0 && (
                    <div className="mt-2">
                      <span className="text-sm text-gray-600">Selected: </span>
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                        {selectedGroup.label}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Prompt Creation */}
            <div className="border-t border-gray-200 pt-4">
              <p className="text-md font-semibold mb-3">Step 2: Add Prompts</p>

              <div className="flex flex-col gap-4">
                {/* Prompt Text */}
                <div className="md:max-w-md">
                  <Input
                    name="promptText"
                    label="Prompt Text"
                    type="text"
                    value={promptText}
                    error={promptError && "Prompt text is required"}
                    placeholder="Enter your prompt here..."
                    
                    onChange={(e) => {
                      setPromptText(e.target.value);
                      setPromptError(false);
                    }}
                  />
                </div>

                {/* Add Prompt Button */}
                <div>
                  <button
                    type="button"
                    className="cursor-pointer font-bold text-sm px-4 py-2 rounded-md bg-primary-500 text-white hover:bg-primary-400 transition-all disabled:opacity-50 flex items-center gap-2"
                    onClick={handleAddNewPrompt}
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Prompt to List
                  </button>
                </div>

                {/* Select from existing prompts */}
                {existingPromptsOptions.length > 0 && (
                  <div className="border-t border-gray-200 pt-4 mt-2">
                    <div className="flex flex-col md:max-w-md">
                      <p className="text-md font-medium mb-1">
                        Or Select from Existing Prompts
                      </p>
                      <p className="text-sm text-gray-500 mb-2">
                        The selected prompt will be added with your selected locations above.
                      </p>
                      <div className="border border-warmgray-300 rounded-md">
                        <MultiSelect
                          key="existingPrompts"
                          options={existingPromptsOptions}
                          onChange={handleSelectExistingPrompt}
                          value={selectedExistingPrompt}
                          menuPlacement="bottom"
                          isLoading={isLoadingExisting}
                          placeholder="Select an existing prompt..."
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Prompts to Submit Section */}
        <div className="border border-gray-200 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <p className="text-lg font-semibold">
              Prompts to Submit ({promptsToSubmit.length})
            </p>
            {promptsToSubmit.length > 0 && (
              <button
                type="button"
                className="text-sm text-red-500 hover:text-red-700 transition-all"
                onClick={handleClearAll}
              >
                Clear All
              </button>
            )}
          </div>

          {promptsToSubmit.length === 0 ? (
            <p className="text-gray-500 italic">
              No prompts added yet. Select locations first, then add prompts above.
            </p>
          ) : (
            <div className="overflow-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="py-2 px-3 text-left text-sm font-semibold text-gray-900 w-12">
                      #
                    </th>
                    <th className="py-2 px-3 text-left text-sm font-semibold text-gray-900">
                      Prompt
                    </th>
                    <th className="py-2 px-3 text-left text-sm font-semibold text-gray-900">
                      Locations
                    </th>
                    <th className="py-2 px-3 text-right text-sm font-semibold text-gray-900 w-20">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {promptsToSubmit.map((p, index) => (
                    <tr key={p.id}>
                      <td className="py-2 px-3 text-sm text-gray-500">
                        {index + 1}
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-900">
                        {p.prompt || p.question}
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-500">
                        <div className="flex flex-wrap gap-1">
                          {p.locationNames?.map((name, i) => (
                            <span
                              key={i}
                              className={`px-2 py-0.5 rounded text-xs ${name === "Global (All Locations)"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100"
                                }`}
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right">
                        <button
                          type="button"
                          className="text-red-500 hover:text-red-700 transition-all"
                          onClick={() => openDeleteConfirmation(index)}
                          title="Remove prompt"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Submit Button */}
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              className="cursor-pointer font-bold text-sm px-6 py-2 rounded-md bg-primary-500 text-white hover:bg-primary-400 transition-all disabled:opacity-50 flex items-center gap-2"
              onClick={handleSubmitPrompts}
              disabled={promptsToSubmit.length === 0 || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="fad fa-circle-notch fa-spin" />
                  Submitting...
                </>
              ) : (
                <>Submit Prompts</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPromptPanel;

